import { useCallback, useEffect, useState, type RefObject } from 'react';

type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FsDocument = Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => Promise<void> | void };

const doc = () => document as FsDocument;
const currentFullscreen = () => doc().fullscreenElement ?? doc().webkitFullscreenElement ?? null;

/**
 * Fullscreen for one element. Uses the Fullscreen API where it exists; where it
 * doesn't (iPhone Safari), falls back to stretching the element over the
 * whole window with the `is-pseudo-fullscreen` class.
 */
export function useFullscreen(ref: RefObject<HTMLElement | null>, beforeEnter?: () => void) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onChange = () => setActive(!!ref.current && currentFullscreen() === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [ref]);

  // The fallback has no browser UI to leave it, so Escape exits.
  useEffect(() => {
    const el = ref.current;
    if (!active || !el?.classList.contains('is-pseudo-fullscreen')) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && toggleOff();
    const toggleOff = () => {
      el.classList.remove('is-pseudo-fullscreen');
      document.documentElement.classList.remove('has-pseudo-fullscreen');
      setActive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, ref]);

  // Leaving the page while in the fallback must not leave the page scroll-locked.
  useEffect(() => () => document.documentElement.classList.remove('has-pseudo-fullscreen'), []);

  const toggle = useCallback(async () => {
    const el = ref.current as FsElement | null;
    if (!el) return;
    if (el.classList.contains('is-pseudo-fullscreen')) {
      el.classList.remove('is-pseudo-fullscreen');
      document.documentElement.classList.remove('has-pseudo-fullscreen');
      setActive(false);
      return;
    }
    if (currentFullscreen() === el) {
      await (doc().exitFullscreen?.() ?? doc().webkitExitFullscreen?.());
      return;
    }
    beforeEnter?.();
    try {
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else throw new Error('unsupported');
    } catch {
      el.classList.add('is-pseudo-fullscreen');
      document.documentElement.classList.add('has-pseudo-fullscreen');
      setActive(true);
    }
  }, [ref, beforeEnter]);

  return { active, toggle };
}

/** Toggles fullscreen for the element in `target`. The compact variant is the little corner button under each game. */
export function FullscreenButton({
  target,
  beforeEnter,
  compact = false,
  className = '',
}: {
  target: RefObject<HTMLElement | null>;
  beforeEnter?: () => void;
  compact?: boolean;
  className?: string;
}) {
  const { active, toggle } = useFullscreen(target, beforeEnter);
  const label = active ? 'Exit fullscreen' : 'Fullscreen';
  return (
    <button
      type="button"
      className={`${compact ? 'fs-corner' : 'icon-btn icon-btn--labelled'} fs-btn ${className}`}
      onClick={() => void toggle()}
      aria-pressed={active}
      aria-label={compact ? label : undefined}
      title={active ? 'Exit fullscreen (Esc)' : 'Fullscreen'}
    >
      <svg viewBox="0 0 24 24" width={compact ? 20 : 18} height={compact ? 20 : 18} aria-hidden="true">
        {active ? (
          <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
      {!compact && label}
    </button>
  );
}
